import { useState, useEffect } from "react";
import { request } from "hooks/useOkoApi/useOkoApi";
import { logEducatorDashboard, LogLevel } from "utils";

// Fallback label shown only until a group's real session_length config loads.
export const DEFAULT_SESSION_LENGTH = 17.5;

// Session length is a real per-group-code config value (used to run the session),
// not a field on the assignment response — so seed it from one assigned group's
// config and persist edits back to every assigned group's config. Shared by the
// lesson and standard bundle views so both headers show + edit the same value.
// `logSource` tags the failure log with the calling view.
export const useSessionLength = (assignments, logSource) => {
  const [sessionLength, setSessionLength] = useState(null);

  useEffect(() => {
    const firstCode = assignments?.[0]?.group_code;
    if (!firstCode) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await request(`group/${firstCode}`, { method: "GET" });
        const len = data?.config?.session_length ?? null;
        if (!cancelled) setSessionLength(len);
      } catch {
        /* leave unset; the accordion falls back to the default label */
      }
    })();
    return () => { cancelled = true; };
  }, [assignments]);

  // Commit immediately on change (independent of the assignment edit/Save flow).
  // It lives in each assigned group's config, so GET-merge-POST to every assigned
  // code; optimistically update, revert on failure. POST replaces the full config,
  // hence the merge.
  const onSessionLengthChange = async (len) => {
    if (len == null || len === sessionLength) return;
    const prev = sessionLength;
    setSessionLength(len);
    try {
      await Promise.all(
        (assignments || []).map(async (a) => {
          const existing = await request(`group/${a.group_code}`, { method: "GET" });
          await request(`group/${a.group_code}`, {
            method: "POST",
            payload: { config: { ...(existing?.config || {}), session_length: len } },
          });
        }),
      );
    } catch (e) {
      setSessionLength(prev);
      logEducatorDashboard(`${logSource}: failed to save session length`, { level: LogLevel.WARNING });
    }
  };

  return { sessionLength, onSessionLengthChange };
};
